// @ts-nocheck
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase, handleSupabaseError } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { 
  Mail, 
  Lock, 
  User, 
  Phone, 
  Loader2, 
  ArrowRight, 
  Shield,
  Key,
  CheckCircle,
  Info
} from "lucide-react";
import { motion } from "framer-motion";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface RegisterFormProps {
  redirectUrl?: string | null;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ redirectUrl }) => {
  const [formData, setFormData] = useState({
    // Personal Information
    email: "",
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: "",
    
    // Employee Verification (Optional)
    isEmployee: false,
    verificationCode: "",
    
    // Shipping Address
    addressLine1: "",
    addressLine2: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "United States",
    
    // Paddle preference
    usePaddleForDisplay: false,
    
    // Agreements
    acceptTerms: false,
  });
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Personal, 2: Verification (optional)
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return false;
    }
    
    if (formData.password !== formData.confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match",
        variant: "destructive",
      });
      return false;
    }
    
    if (formData.password.length < 6) {
      toast({
        title: "Weak Password",
        description: "Password must be at least 6 characters",
        variant: "destructive",
      });
      return false;
    }
    
    // Require shipping address
    if (!formData.addressLine1 || !formData.city || !formData.stateProvince || !formData.postalCode || !formData.country) {
      toast({
        title: "Shipping Address Required",
        description: "Please enter your full shipping address",
        variant: "destructive",
      });
      return false;
    }
    
    if (!formData.acceptTerms) {
      toast({
        title: "Agreement Required",
        description: "Please accept the terms and conditions to continue",
        variant: "destructive",
      });
      return false;
    }
    
    return true;
  };

  async function handleSignUp() {
    if (!formData.acceptTerms) {
      toast({
        title: "Agreement Required",
        description: "Please accept the terms and conditions",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      // Note: In production, card details should be tokenized with Stripe on the client
      // and only the token should be sent to your backend
      
      // Sign up the user
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
            use_paddle_for_display: formData.usePaddleForDisplay,
          }
        }
      });

      if (signUpError) throw signUpError;

      if (authData.user) {
        // Create profile with payment info placeholder
        // In production, store Stripe customer ID here instead of card details
        const { error: profileError } = await supabase
          .from('profiles')
          .insert({
            id: authData.user.id,
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
            is_employee: formData.isEmployee,
            // In production: stripe_customer_id would be stored here
            // Card details should NEVER be stored directly in database
          });

        if (profileError && profileError.code !== '23505') {
          console.error('Profile creation error:', profileError);
        }
        
        // If employee with verification code, validate and apply permissions
        if (formData.isEmployee && verificationResult) {
          const { data: validationResult, error: validationError } = await supabase
            .rpc('validate_verification_code', {
              p_code: formData.verificationCode.toUpperCase(),
              p_user_id: authData.user.id
            });
          
          if (validationError) {
            console.error('Failed to apply employee permissions:', validationError);
          } else if (validationResult?.success) {
            console.log('Employee permissions applied:', validationResult);
          }
        }
        
        // Save primary shipping address
        try {
          const { error: addrError } = await supabase
            .from('user_addresses')
            .insert({
              user_id: authData.user.id,
              address_type: 'shipping',
              label: 'Primary',
              first_name: formData.firstName,
              last_name: formData.lastName,
              company: null,
              address_line_1: formData.addressLine1,
              address_line_2: formData.addressLine2 || null,
              city: formData.city,
              state_province: formData.stateProvince,
              postal_code: formData.postalCode,
              country: formData.country,
              phone: formData.phone || null,
              is_primary: true,
            } as any);
          if (addrError) console.error('Address save error:', addrError);
        } catch (e) {
          console.error('Unexpected address save error:', e);
        }

        toast({
          title: "Welcome to Florida Highwaymen Auction!",
          description: "Your account has been created successfully.",
        });
      }
      
      // Navigate to the redirect URL if provided, otherwise go to home
      const destination = redirectUrl || "/";
      navigate(destination);
    } catch (error: any) {
      const errorMessage = handleSupabaseError(error);
      toast({
        title: "Registration Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const verifyCode = async () => {
    if (!formData.verificationCode) {
      toast({
        title: "No Code Entered",
        description: "Please enter a verification code or uncheck employee registration",
        variant: "destructive",
      });
      return false;
    }

    setVerifyingCode(true);
    try {
      // Check if code is valid
      const { data: codeData, error: codeError } = await supabase
        .from('employee_verification_codes')
        .select('*')
        .eq('code', formData.verificationCode.toUpperCase())
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (codeError || !codeData) {
        toast({
          title: "Invalid Code",
          description: "The verification code is invalid or expired",
          variant: "destructive",
        });
        return false;
      }

      if (codeData.times_used >= codeData.max_uses) {
        toast({
          title: "Code Already Used",
          description: "This verification code has reached its maximum uses",
          variant: "destructive",
        });
        return false;
      }

      setVerificationResult(codeData);
      toast({
        title: "✅ Code Verified",
        description: `Employee access granted with ${codeData.tab_permissions.length} tab permissions`,
      });
      return true;
    } catch (error: any) {
      toast({
        title: "Verification Failed",
        description: error.message || "Failed to verify code",
        variant: "destructive",
      });
      return false;
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      if (validateStep1()) {
        // If employee checkbox is checked, go to verification step
        if (formData.isEmployee) {
          setStep(2);
        } else {
          handleSignUp();
        }
      }
    } else if (step === 2) {
      // Verify the code before completing registration
      const isValid = await verifyCode();
      if (isValid) {
        handleSignUp();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} autoComplete="on" className="space-y-5">
      {step === 1 ? (
        // Step 1: Personal Information
        <>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="space-y-2"
              >
                <Label htmlFor="firstName" className="text-white/80 font-light text-sm">
                  First Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="space-y-2"
              >
                <Label htmlFor="lastName" className="text-white/80 font-light text-sm">
                  Last Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="space-y-2"
            >
              <Label htmlFor="email" className="text-white/80 font-light text-sm">
                Email Address *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="space-y-2"
            >
              <Label htmlFor="phone" className="text-white/80 font-light text-sm">
                Phone Number *
              </Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                />
              </div>
            </motion.div>

            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-2"
              >
                <Label htmlFor="password" className="text-white/80 font-light text-sm">
                  Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="space-y-2"
              >
                <Label htmlFor="confirmPassword" className="text-white/80 font-light text-sm">
                  Confirm Password *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                </div>
              </motion.div>
            </div>

            {/* Employee Registration Option */}
            {/* Shipping Address */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
              className="space-y-2"
            >
              <Label className="text-white/80 font-light text-sm">Shipping Address</Label>
              <div className="grid grid-cols-1 gap-3">
                <Input
                  id="addressLine1"
                  name="addressLine1"
                  type="text"
                  placeholder="Address line 1"
                  value={formData.addressLine1}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                />
                <Input
                  id="addressLine2"
                  name="addressLine2"
                  type="text"
                  placeholder="Address line 2 (optional)"
                  value={formData.addressLine2}
                  onChange={handleChange}
                  disabled={loading}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                />
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="City"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                  <Input
                    id="stateProvince"
                    name="stateProvince"
                    type="text"
                    placeholder="State / Province"
                    value={formData.stateProvince}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                  <Input
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    placeholder="ZIP / Postal Code"
                    value={formData.postalCode}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                  />
                </div>
                <Input
                  id="country"
                  name="country"
                  type="text"
                  placeholder="Country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/20"
            >
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="isEmployee"
                  checked={formData.isEmployee}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, isEmployee: checked as boolean, verificationCode: '' }))
                  }
                  className="mt-1 border-white/20 data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                />
                <div className="flex-1">
                  <label htmlFor="isEmployee" className="text-white/80 font-medium cursor-pointer">
                    Register as Employee
                  </label>
                  <p className="text-xs text-white/60 mt-1">
                    Check this if you have an employee verification code from your administrator
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Paddle Preference */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45 }}
              className="p-4 bg-white/5 rounded-xl border border-white/10"
            >
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="usePaddleForDisplay"
                  checked={formData.usePaddleForDisplay}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, usePaddleForDisplay: checked as boolean }))
                  }
                  className="mt-1 border-white/20 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                />
                <div className="flex-1">
                  <label htmlFor="usePaddleForDisplay" className="text-white/80 font-medium cursor-pointer">
                    Use Paddle # for display (privacy)
                  </label>
                  <p className="text-xs text-white/60">
                    We'll assign your paddle number now and show it instead of your name where applicable.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Terms and Payment Info Notice */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.48 }}
            className="space-y-4"
          >
            <div className="flex items-start space-x-2">
              <Checkbox
                id="acceptTerms"
                checked={formData.acceptTerms}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, acceptTerms: checked as boolean }))
                }
                className="mt-1 border-white/20 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
              />
              <label htmlFor="acceptTerms" className="text-white/60 text-sm font-light cursor-pointer">
                I agree to the{" "}
                <a href="/terms" target="_blank" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                  Terms of Service
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                  Privacy Policy
                </a>
              </label>
            </div>

            <Alert className="bg-blue-500/10 border-blue-500/20 text-white">
              <Info className="h-4 w-4 text-blue-300" />
              <AlertDescription className="text-white/80 text-sm">
                You can securely add or update your payment method anytime from your account dashboard after registration.
              </AlertDescription>
            </Alert>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-medium rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-300 border-0"
            >
              {formData.isEmployee ? 'Continue to Verification' : 'Complete Registration'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </motion.div>
        </>
      ) : step === 2 ? (
        // Step 2: Employee Verification
        <>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <Alert className="bg-blue-500/10 border-blue-500/20 text-white">
              <Shield className="h-4 w-4 text-blue-400" />
              <AlertDescription className="text-white/80">
                Enter the verification code provided by your administrator to gain employee access.
              </AlertDescription>
            </Alert>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="space-y-2"
            >
              <Label htmlFor="verificationCode" className="text-white/80 font-light text-sm">
                Employee Verification Code *
              </Label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  placeholder="Enter 8-character code"
                  value={formData.verificationCode}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    verificationCode: e.target.value.toUpperCase().substring(0, 8) 
                  }))}
                  maxLength={8}
                  required
                  disabled={loading || verifyingCode}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-blue-500/50 focus:ring-blue-500/20 h-11 rounded-xl font-mono uppercase"
                />
              </div>
              <p className="text-xs text-white/60">
                The code should be 8 characters long (letters and numbers)
              </p>
            </motion.div>

            {verificationResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-green-500/10 rounded-xl border border-green-500/20"
              >
                <div className="flex items-start space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                  <div>
                    <p className="text-white/80 font-medium">Code Verified Successfully!</p>
                    <p className="text-xs text-white/60 mt-1">
                      You will have access to {verificationResult.tab_permissions.length} admin tabs
                    </p>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>

          <div className="flex gap-3">
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex-1"
            >
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                disabled={loading || verifyingCode}
                className="w-full h-12 bg-white/5 border-white/20 text-white hover:bg-white/10 rounded-xl"
              >
                Back
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex-1"
            >
              <Button
                type="submit"
                disabled={loading || verifyingCode || !formData.verificationCode}
                className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-medium rounded-xl shadow-lg hover:shadow-blue-500/25 transition-all duration-300 border-0"
              >
                {verifyingCode ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Complete Registration'
                )}
              </Button>
            </motion.div>
          </div>
        </>
      ) : (
        null
      )}

      {/* Progress Indicator */}
      <div className="flex justify-center space-x-2 pt-4">
        <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
          step >= 1 ? 'bg-emerald-500 w-8' : 'bg-white/20'
        }`} />
        {formData.isEmployee && (
          <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
            step >= 2 ? 'bg-emerald-500 w-8' : 'bg-white/20'
          }`} />
        )}
        {!formData.isEmployee && (
          <div className={`h-2 w-2 rounded-full transition-all duration-300 ${
            step >= 2 ? 'bg-emerald-500 w-8' : 'bg-white/20'
          }`} />
        )}
      </div>
    </form>
  );
};

export default RegisterForm;